import { webFileToPickedFile } from '@tinycld/core/file-viewer/picked-file'
import { uploadRecordWithFile } from '@tinycld/core/file-viewer/upload-file'
import { usePickFiles } from '@tinycld/core/file-viewer/use-pick-files'
import { captureException } from '@tinycld/core/lib/errors'
import { performMutations, useMutation } from '@tinycld/core/lib/mutations'
import { pb, useStore } from '@tinycld/core/lib/pocketbase'
import { newRecordId } from 'pbtsdb/core'
import { useCallback, useRef } from 'react'
import { Platform } from 'react-native'
import { type UploadingFile, useUploadStore } from '../stores/upload-store'

export type { UploadingFile, UploadStatus } from '../stores/upload-store'

export interface DroppedEntry {
    path: string
    file: File | null // null = directory
}

interface UseFileUploadOptions {
    userId: string
    currentFolderId: string
}

const DONE_AUTO_CLEAR_MS = 3000
const PROGRESS_THROTTLE_MS = 60

// Extension → MIME fallback for uploads whose browser-reported `file.type`
// is empty. Browsers are inconsistent here — .rtf in particular often
// arrives with no type — and the server keys document features (thumbnail,
// full-text index, in-app preview/open) off the stored mime, so an
// octet-stream upload silently loses all of them. Only the document types
// omnidoc can process need this; images/media are handled elsewhere.
const UPLOAD_MIME_BY_EXT: Record<string, string> = {
    rtf: 'application/rtf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    epub: 'application/epub+zip',
    csv: 'text/csv',
    md: 'text/markdown',
    markdown: 'text/markdown',
}

function mimeForUpload(file: File): string {
    if (file.type) return file.type
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    return UPLOAD_MIME_BY_EXT[ext] ?? 'application/octet-stream'
}

export function useFileUpload({ userId, currentFolderId }: UseFileUploadOptions) {
    // Intentionally NOT subscribing to uploadingFiles here. useFileUpload runs
    // inside useDriveState, so any reactive read of upload progress would force
    // every useDrive() consumer (toolbar, dialogs, layout, sidebar) to re-render
    // on every progress tick (~60ms throttle). Components that need to display
    // upload state read useUploadStore directly; the mutation pipeline below
    // writes via getState().
    const folderRef = useRef(currentFolderId)
    folderRef.current = currentFolderId
    const [itemsCollection] = useStore('drive_items')
    const { pickFiles } = usePickFiles()

    const updateFile = useCallback((id: string, patch: Partial<UploadingFile>) => {
        useUploadStore.getState().update(id, patch)
    }, [])

    const dismissUpload = useCallback((id: string) => {
        useUploadStore.getState().remove(id)
    }, [])

    const scheduleClearDone = useCallback((id: string) => {
        setTimeout(() => {
            useUploadStore.getState().clearDoneById(id)
        }, DONE_AUTO_CLEAR_MS)
    }, [])

    // Throttled progress writer: at most one update per ~60ms per file, plus a
    // final flush when bytes reach total so the bar always lands on 100%.
    const makeProgressHandler = useCallback(
        (id: string) => {
            let lastUpdate = 0
            return (loaded: number, total: number) => {
                const now = Date.now()
                const isFinal = total > 0 && loaded >= total
                if (!isFinal && now - lastUpdate < PROGRESS_THROTTLE_MS) return
                lastUpdate = now
                updateFile(id, total > 0 ? { loaded, size: total } : { loaded })
            }
        },
        [updateFile]
    )

    const uploadOne = useCallback(
        async (params: { id: string; name: string; parentId: string; file: File }) => {
            const { id, name, parentId, file } = params
            updateFile(id, { status: 'uploading', loaded: 0 })

            const response = await uploadRecordWithFile({
                collection: 'drive_items',
                fields: {
                    id,
                    name,
                    is_folder: 'false',
                    mime_type: mimeForUpload(file),
                    parent: parentId,
                    created_by: userId,
                    size: String(file.size),
                    description: '',
                },
                file: webFileToPickedFile(file),
                onProgress: makeProgressHandler(id),
            })

            const finalName =
                response &&
                typeof response === 'object' &&
                'name' in response &&
                typeof response.name === 'string'
                    ? response.name
                    : name
            updateFile(id, { status: 'done', loaded: file.size, name: finalName })
            scheduleClearDone(id)
        },
        [userId, makeProgressHandler, updateFile, scheduleClearDone]
    )

    const uploadMutation = useMutation({
        mutationFn: async (files: File[]) => {
            const parentId = folderRef.current
            const queued: UploadingFile[] = files.map(f => ({
                id: newRecordId(),
                name: f.name,
                parentId,
                size: f.size,
                loaded: 0,
                status: 'pending',
            }))
            useUploadStore.getState().add(queued)

            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                const entry = queued[i]
                try {
                    await uploadOne({ id: entry.id, name: entry.name, parentId, file })
                } catch (err) {
                    const message = err instanceof Error ? err.message : 'Upload failed'
                    updateFile(entry.id, { status: 'error', errorMessage: message })
                    captureException('useFileUpload', err)
                    throw err
                }
            }
        },
    })

    const uploadFiles = useCallback(
        (files: File[]) => {
            if (files.length === 0) return
            uploadMutation.mutate(files)
        },
        [uploadMutation]
    )

    const triggerFilePicker = useCallback(async () => {
        const picked = await pickFiles({ sources: ['documents'], multiple: true })
        if (picked.length > 0) uploadFiles(picked.map(p => p.file))
    }, [pickFiles, uploadFiles])

    const triggerPhotoPicker = useCallback(async () => {
        if (Platform.OS === 'web') return
        const picked = await pickFiles({ sources: ['photoLibrary'], multiple: true })
        if (picked.length > 0) uploadFiles(picked.map(p => p.file))
    }, [pickFiles, uploadFiles])

    const uploadTreeMutation = useMutation({
        mutationFn: async (entries: DroppedEntry[]) => {
            const parentId = folderRef.current
            const fileEntries = entries.filter(e => e.file)
            const queued: UploadingFile[] = fileEntries.map(e => ({
                id: newRecordId(),
                name: e.path,
                parentId,
                size: e.file?.size ?? 0,
                loaded: 0,
                status: 'pending',
            }))
            useUploadStore.getState().add(queued)
            const queuedById = new Map(fileEntries.map((e, i) => [e.path, queued[i]]))

            // Map directory path -> PocketBase record ID
            const folderIds = new Map<string, string>()

            // Sort entries so directories come before their children
            const sorted = [...entries].sort((a, b) => {
                const aDepth = a.path.split('/').length
                const bDepth = b.path.split('/').length
                if (aDepth !== bDepth) return aDepth - bDepth
                return a.path.localeCompare(b.path)
            })

            for (const entry of sorted) {
                const segments = entry.path.split('/')
                const name = segments[segments.length - 1]
                const parentPath = segments.slice(0, -1).join('/')
                const localParentId = parentPath ? (folderIds.get(parentPath) ?? '') : parentId

                if (!entry.file) {
                    const folderId = newRecordId()
                    folderIds.set(entry.path, folderId)

                    await performMutations(function* () {
                        yield itemsCollection.insert({
                            id: folderId,
                            name,
                            is_folder: true,
                            mime_type: '',
                            parent: localParentId,
                            created_by: userId,
                            size: 0,
                            file: '',
                            description: '',
                        })
                    })
                } else {
                    const queuedEntry = queuedById.get(entry.path)
                    if (!queuedEntry) continue

                    try {
                        await uploadOne({
                            id: queuedEntry.id,
                            name,
                            parentId: localParentId,
                            file: entry.file,
                        })
                    } catch (err) {
                        const message = err instanceof Error ? err.message : 'Upload failed'
                        updateFile(queuedEntry.id, { status: 'error', errorMessage: message })
                        captureException('useFileUpload.uploadTree', err)
                        throw err
                    }
                }
            }
        },
    })

    const uploadTree = useCallback(
        (entries: DroppedEntry[]) => {
            if (entries.length === 0) return
            uploadTreeMutation.mutate(entries)
        },
        [uploadTreeMutation]
    )

    const uploadNewVersion = useCallback(async (itemId: string, file: File) => {
        const formData = new FormData()
        formData.append('item', itemId)
        formData.append('file', file)
        await pb.send('/api/drive/upload-version', {
            method: 'POST',
            body: formData,
        })
    }, [])

    return {
        uploadFiles,
        uploadTree,
        dismissUpload,
        triggerFilePicker,
        triggerPhotoPicker,
        uploadNewVersion,
    }
}
